import React, { useState, useRef, useCallback } from 'react';

// ============================================
// TYPES
// ============================================

interface ApplicationLogUploadModalProps {
    isOpen: boolean;
    onClose: () => void;
    cvr: string;
    marknr: string;
    onUploadSuccess: () => void;
}

interface UploadResult {
    success: boolean;
    error?: string;
    // Multi-log support (ny backend response)
    totalLogs?: number;
    logs?: any[];
    metadata?: {
        tractor: string | null;
        equipment: string | null;
        totalDurationMinutes: number | null;
        unit: string | null;
        applicationDate: string | null;
    };
    // Single-log (backwards compat)
    log?: {
        id: number;
        applicationDate: string;
        productType: string;
        detectedTractor: string | null;
        detectedEquipment: string | null;
        detectedDurationMinutes: number | null;
        detectedUnit: string | null;
    };
    geometryMatch?: {
        overlapPercent: number | null;
        status: 'ok' | 'warning' | 'error' | 'unknown';
        message: string;
    };
}

type UploadState = 'IDLE' | 'UPLOADING' | 'PREVIEW' | 'SUCCESS' | 'ERROR';

// ============================================
// KONSTANTER
// ============================================

const PRODUCT_TYPES = [
    { value: 'nitrogen', label: 'Kvælstof (N)' },
    { value: 'phosphor', label: 'Fosfor (P)' },
    { value: 'potassium', label: 'Kalium (K)' },
    { value: 'npk', label: 'NPK' },
    { value: 'pesticide', label: 'Pesticid' },
    { value: 'herbicide', label: 'Herbicid' },
    { value: 'fungicide', label: 'Fungicid' },
    { value: 'growth', label: 'Vækstregulering' },
    { value: 'lime', label: 'Kalk' },
    { value: 'other', label: 'Andet' }
];

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4002';

// ============================================
// HELPER FUNKTIONER
// ============================================

function formatDuration(minutes: number | null): string {
    if (minutes === null) return '-';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours === 0) return `${mins} min`;
    if (mins === 0) return `${hours} timer`;
    return `${hours} t ${mins} min`;
}

/**
 * Normaliserer backend response til fælles format
 * Håndterer både gammel (single log) og ny (multi-log) struktur
 */
function normalizeResponse(data: any): UploadResult {
    // Ny multi-log struktur
    if (data.logs && Array.isArray(data.logs) && data.logs.length > 0) {
        const primaryLog = data.logs[0];
        const pct = primaryLog.geometryMatchPercent;

        // Beregn status fra procent
        let status: 'ok' | 'warning' | 'error' = 'error';
        if (pct !== null && pct !== undefined) {
            if (pct >= 80) status = 'ok';
            else if (pct >= 50) status = 'warning';
        }

        return {
            success: true,
            totalLogs: data.totalLogs || data.logs.length,
            logs: data.logs,
            metadata: data.metadata,
            // Map til gammel struktur for rendering
            log: {
                id: primaryLog.id,
                applicationDate: data.metadata?.applicationDate || '',
                productType: '',
                detectedTractor: data.metadata?.tractor || null,
                detectedEquipment: data.metadata?.equipment || null,
                detectedDurationMinutes: data.metadata?.totalDurationMinutes || null,
                detectedUnit: data.metadata?.unit || null,
            },
            geometryMatch: {
                overlapPercent: pct ?? null,
                status: status,
                message: status === 'error' 
                    ? 'Mark ikke fundet' 
                    : 'Filen er færdiganalyseret. Klik på Gem log for at gemme logfilerne på de identificerede marker.'
            }
        };
    }

    // Gammel single-log struktur - returner uændret
    return data as UploadResult;
}

// ============================================
// KOMPONENT
// ============================================

const ApplicationLogUploadModal: React.FC<ApplicationLogUploadModalProps> = ({
    isOpen,
    onClose,
    cvr,
    marknr,
    onUploadSuccess
}) => {
    const [state, setState] = useState<UploadState>('IDLE');
    const [file, setFile] = useState<File | null>(null);
    const [result, setResult] = useState<UploadResult | null>(null);
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [productType, setProductType] = useState<string>('nitrogen');
    const [applicationDate, setApplicationDate] = useState<string>('');
    const [isDragging, setIsDragging] = useState(false);

    const fileInputRef = useRef<HTMLInputElement>(null);

    // Reset modal state
    const resetState = useCallback(() => {
        setState('IDLE');
        setFile(null);
        setResult(null);
        setErrorMessage('');
        setProductType('nitrogen');
        setApplicationDate('');
        setIsDragging(false);
    }, []);

    // Handle close
    const handleClose = () => {
        resetState();
        onClose();
    };

    // Handle file selection
    const handleFileSelect = async (selectedFile: File) => {
        if (!selectedFile.name.toLowerCase().endsWith('.zip')) {
            setErrorMessage('Kun .zip filer er tilladt');
            setState('ERROR');
            return;
        }

        setFile(selectedFile);
        setState('UPLOADING');

        try {
            const formData = new FormData();
            formData.append('file', selectedFile);
            formData.append('cvr', cvr);
            formData.append('marknr', marknr);
            formData.append('productType', productType);

            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/api/application-logs/upload`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                body: formData
            });

            const rawData = await response.json();

            if (!response.ok || !rawData.success) {
                setErrorMessage(rawData.error || 'Upload fejlede');
                setState('ERROR');
                return;
            }

            // Normaliser response til fælles format
            const data = normalizeResponse(rawData);
            setResult(data);

            // Pre-fill application date from detected data
            const detectedDate = data.log?.applicationDate || data.metadata?.applicationDate;
            if (detectedDate) {
                setApplicationDate(detectedDate.split('T')[0]);
            } else {
                setApplicationDate(new Date().toISOString().split('T')[0]);
            }

            setState('PREVIEW');

        } catch (error) {
            console.error('Upload error:', error);
            setErrorMessage('Netværksfejl - prøv igen');
            setState('ERROR');
        }
    };

    // Handle drag events
    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
        const droppedFile = e.dataTransfer.files[0];
        if (droppedFile) {
            handleFileSelect(droppedFile);
        }
    };

    // Handle file input change
    const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFile = e.target.files?.[0];
        if (selectedFile) {
            handleFileSelect(selectedFile);
        }
    };

    // Handle save - opdater ProductType via PATCH endpoint
    const handleSave = async () => {
        if (!result?.log && !result?.logs?.length) return;

        const token = localStorage.getItem('token');

        // Saml alle log IDs (filter undefined væk)
        const logIds: number[] = [];

        if (result.logs && result.logs.length > 0) {
            result.logs.forEach((l: any) => {
                if (l.id) logIds.push(l.id);
            });
        } else if (result.log?.id) {
            logIds.push(result.log.id);
        }

        // Hvis ingen logs at opdatere, bare luk
        if (logIds.length === 0) {
            onUploadSuccess();
            handleClose();
            return;
        }

        try {
            // Opdater ProductType for alle logs via PATCH
            for (const logId of logIds) {
                const response = await fetch(`${API_URL}/api/application-logs/${logId}`, {
                    method: 'PATCH',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({ productType })
                });

                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    throw new Error(errorData.error || `Fejl ved opdatering af log ${logId}`);
                }
            }

            console.log(`[Upload] ProductType opdateret til '${productType}' for ${logIds.length} log(s)`);

            onUploadSuccess();
            handleClose();

        } catch (error: any) {
            console.error('Fejl ved opdatering af ProductType:', error);
            setErrorMessage(error.message || 'Kunne ikke gemme produkttype');
            setState('ERROR');
        }
    };

    // Render geometry match box
    const renderGeometryMatch = () => {
        if (!result?.geometryMatch) return null;

        const { status, message, overlapPercent } = result.geometryMatch;

        let boxClass = 'match-box';
        let icon = '✓';

        if (status === 'ok') {
            boxClass += ' match-ok';
            icon = '✓';
        } else if (status === 'warning') {
            boxClass += ' match-warning';
            icon = '⚠';
        } else {
            boxClass += ' match-error';
            icon = '✗';
        }

        return (
            <div className={boxClass}>
                <span className="match-icon">{icon}</span>
                <div className="match-text">
                    <span className="match-message">{message}</span>
                    {overlapPercent !== null && (
                        <span className="match-percent">{Math.round(overlapPercent)}% overlap</span>
                    )}
                </div>
            </div>
        );
    };

    // Render detected info
    const renderDetectedInfo = () => {
        if (!result?.log) return null;

        const { detectedTractor, detectedEquipment, detectedDurationMinutes, detectedUnit } = result.log;
        const totalLogs = result.totalLogs || 1;

        return (
            <div className="detected-box">
                <div className="detected-header">
                    <span className="auto-badge">AUTO</span>
                    <span className="detected-title">Detekteret fra fil</span>
                    {totalLogs > 1 && (
                        <span className="logs-badge">{totalLogs} logs</span>
                    )}
                </div>
                <div className="detected-grid">
                    <div className="detected-item">
                        <span className="detected-label">Traktor</span>
                        <span className="detected-value">{detectedTractor || '-'}</span>
                    </div>
                    <div className="detected-item">
                        <span className="detected-label">Udstyr</span>
                        <span className="detected-value">{detectedEquipment || '-'}</span>
                    </div>
                    <div className="detected-item">
                        <span className="detected-label">Varighed</span>
                        <span className="detected-value">{formatDuration(detectedDurationMinutes)}</span>
                    </div>
                    <div className="detected-item">
                        <span className="detected-label">Enhed</span>
                        <span className="detected-value">{detectedUnit || '-'}</span>
                    </div>
                </div>
                {/* Vis marker liste hvis multi-log */}
                {result.logs && result.logs.length >= 1 && (
                    <div className="logs-list">
                        <span className="logs-list-label">Fordelt på marker:</span>
                        <div className="logs-chips">
                            {result.logs.slice(0, 5).map((log: any) => (
                                <span key={log.id} className="log-chip">
                                    {log.marknr}
                                </span>
                            ))}
                            {result.logs.length > 5 && (
                                <span className="log-chip log-chip-more">+{result.logs.length - 5}</span>
                            )}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    if (!isOpen) return null;

    const canSave = state === 'PREVIEW' &&
        applicationDate &&
        productType &&
        result?.geometryMatch?.status !== 'error';

    const totalLogs = result?.totalLogs || 1;

    return (
        <div className="modal-overlay" onClick={handleClose}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="modal-header">
                    <h3>Upload tildelingslog</h3>
                    <button className="close-btn" onClick={handleClose}>✕</button>
                </div>

                {/* Content */}
                <div className="modal-content">
                    {/* IDLE State - Dropzone */}
                    {state === 'IDLE' && (
                        <div
                            className={`dropzone ${isDragging ? 'dropzone-active' : ''}`}
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                            onClick={() => fileInputRef.current?.click()}
                        >
                            <span className="dropzone-icon">📄</span>
                            <span className="dropzone-text">
                                Træk as-applied i zip format hertil
                            </span>
                            <span className="dropzone-subtext">
                                eller klik for at vælge fil
                            </span>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".zip"
                                onChange={handleFileInputChange}
                                style={{ display: 'none' }}
                            />
                        </div>
                    )}

                    {/* UPLOADING State */}
                    {state === 'UPLOADING' && (
                        <div className="uploading-box">
                            <div className="spinner"></div>
                            <span className="uploading-text">Uploader og analyserer...</span>
                            {file && (
                                <span className="uploading-filename">{file.name}</span>
                            )}
                        </div>
                    )}

                    {/* PREVIEW State */}
                    {state === 'PREVIEW' && (
                        <div className="preview-content">
                            {renderGeometryMatch()}
                            {renderDetectedInfo()}

                            <div className="form-section">
                                <div className="form-group">
                                    <label className="form-label">Tildelingsdato</label>
                                    <input
                                        type="date"
                                        className="form-input"
                                        value={applicationDate}
                                        onChange={(e) => setApplicationDate(e.target.value)}
                                    />
                                </div>

                                <div className="form-group">
                                    <label className="form-label">Produkttype</label>
                                    <select
                                        className="form-input"
                                        value={productType}
                                        onChange={(e) => setProductType(e.target.value)}
                                    >
                                        {PRODUCT_TYPES.map((type) => (
                                            <option key={type.value} value={type.value}>
                                                {type.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* SUCCESS State */}
                    {state === 'SUCCESS' && (
                        <div className="success-box">
                            <span className="success-icon">✓</span>
                            <span className="success-text">
                                {totalLogs > 1 ? `${totalLogs} logs gemt!` : 'Log gemt!'}
                            </span>
                        </div>
                    )}

                    {/* ERROR State */}
                    {state === 'ERROR' && (
                        <div className="error-box">
                            <span className="error-icon">✗</span>
                            <span className="error-text">{errorMessage}</span>
                            <button
                                className="retry-btn"
                                onClick={resetState}
                            >
                                Prøv igen
                            </button>
                        </div>
                    )}
                </div>

                {/* Footer */}
                {state === 'PREVIEW' && (
                    <div className="modal-footer">
                        <button className="cancel-btn" onClick={handleClose}>
                            Annuller
                        </button>
                        <button
                            className="save-btn"
                            onClick={handleSave}
                            disabled={!canSave}
                        >
                            {totalLogs > 1 ? `Gem ${totalLogs} logs` : 'Gem log'}
                        </button>
                    </div>
                )}

                <style>{`
                    .modal-overlay {
                        position: fixed;
                        top: 0;
                        left: 0;
                        right: 0;
                        bottom: 0;
                        background: rgba(0, 0, 0, 0.5);
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        z-index: 1000;
                    }

                    .modal {
                        background: white;
                        width: 100%;
                        max-width: 440px;
                        border-radius: 12px;
                        box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1),
                                    0 10px 10px -5px rgba(0, 0, 0, 0.04);
                        overflow: hidden;
                    }

                    .modal-header {
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                        padding: 16px 20px;
                        border-bottom: 1px solid #e5e7eb;
                    }

                    .modal-header h3 {
                        margin: 0;
                        font-size: 16px;
                        font-weight: 600;
                        color: #374151;
                    }

                    .close-btn {
                        background: none;
                        border: none;
                        font-size: 18px;
                        color: #6b7280;
                        cursor: pointer;
                        padding: 4px;
                    }

                    .close-btn:hover {
                        color: #374151;
                    }

                    .modal-content {
                        padding: 20px;
                    }

                    /* Dropzone */
                    .dropzone {
                        display: flex;
                        flex-direction: column;
                        align-items: center;
                        justify-content: center;
                        padding: 40px 20px;
                        border: 2px dashed #e5e7eb;
                        border-radius: 8px;
                        cursor: pointer;
                        transition: all 0.15s ease;
                    }

                    .dropzone:hover,
                    .dropzone-active {
                        border-color: #16a34a;
                        background: #f0fdf4;
                    }

                    .dropzone-icon {
                        font-size: 40px;
                        margin-bottom: 12px;
                    }

                    .dropzone-text {
                        font-size: 14px;
                        font-weight: 500;
                        color: #374151;
                    }

                    .dropzone-subtext {
                        font-size: 12px;
                        color: #6b7280;
                        margin-top: 4px;
                    }

                    /* Uploading */
                    .uploading-box {
                        display: flex;
                        flex-direction: column;
                        align-items: center;
                        padding: 40px 20px;
                    }

                    .spinner {
                        width: 40px;
                        height: 40px;
                        border: 3px solid #e5e7eb;
                        border-top-color: #16a34a;
                        border-radius: 50%;
                        animation: spin 1s linear infinite;
                    }

                    @keyframes spin {
                        to { transform: rotate(360deg); }
                    }

                    .uploading-text {
                        font-size: 14px;
                        font-weight: 500;
                        color: #374151;
                        margin-top: 16px;
                    }

                    .uploading-filename {
                        font-size: 12px;
                        color: #6b7280;
                        margin-top: 4px;
                    }

                    /* Preview */
                    .preview-content {
                        display: flex;
                        flex-direction: column;
                        gap: 16px;
                    }

                    /* Match box */
                    .match-box {
                        display: flex;
                        align-items: center;
                        gap: 12px;
                        padding: 12px 16px;
                        border-radius: 8px;
                        border: 1px solid;
                    }

                    .match-ok {
                        background: #dcfce7;
                        border-color: #16a34a;
                    }

                    .match-warning {
                        background: #fef3c7;
                        border-color: #f59e0b;
                    }

                    .match-error {
                        background: #fee2e2;
                        border-color: #dc2626;
                    }

                    .match-icon {
                        font-size: 20px;
                    }

                    .match-ok .match-icon { color: #16a34a; }
                    .match-warning .match-icon { color: #f59e0b; }
                    .match-error .match-icon { color: #dc2626; }

                    .match-text {
                        display: flex;
                        flex-direction: column;
                        gap: 2px;
                    }

                    .match-message {
                        font-size: 14px;
                        font-weight: 500;
                        color: #374151;
                    }

                    .match-percent {
                        font-size: 12px;
                        color: #6b7280;
                    }

                    /* Detected box */
                    .detected-box {
                        background: #f9fafb;
                        border: 1px solid #e5e7eb;
                        border-radius: 8px;
                        padding: 12px 16px;
                    }

                    .detected-header {
                        display: flex;
                        align-items: center;
                        gap: 8px;
                        margin-bottom: 12px;
                    }

                    .auto-badge {
                        background: #dcfce7;
                        color: #166534;
                        font-size: 10px;
                        font-weight: 600;
                        padding: 2px 6px;
                        border-radius: 4px;
                    }

                    .logs-badge {
                        background: #dbeafe;
                        color: #1e40af;
                        font-size: 10px;
                        font-weight: 600;
                        padding: 2px 6px;
                        border-radius: 4px;
                        margin-left: auto;
                    }

                    .detected-title {
                        font-size: 12px;
                        color: #6b7280;
                    }

                    .detected-grid {
                        display: grid;
                        grid-template-columns: 1fr 1fr;
                        gap: 12px;
                    }

                    .detected-item {
                        display: flex;
                        flex-direction: column;
                        gap: 2px;
                    }

                    .detected-label {
                        font-size: 11px;
                        color: #6b7280;
                    }

                    .detected-value {
                        font-size: 13px;
                        color: #374151;
                        font-weight: 500;
                    }

                    /* Logs list for multi-log */
                    .logs-list {
                        margin-top: 12px;
                        padding-top: 12px;
                        border-top: 1px solid #e5e7eb;
                    }

                    .logs-list-label {
                        font-size: 11px;
                        color: #6b7280;
                        display: block;
                        margin-bottom: 8px;
                    }

                    .logs-chips {
                        display: flex;
                        flex-wrap: wrap;
                        gap: 6px;
                    }

                    .log-chip {
                        background: white;
                        border: 1px solid #e5e7eb;
                        border-radius: 4px;
                        padding: 4px 8px;
                        font-size: 12px;
                        color: #374151;
                    }

                    .log-chip-more {
                        background: #f3f4f6;
                        color: #6b7280;
                    }

                    /* Form */
                    .form-section {
                        display: flex;
                        flex-direction: column;
                        gap: 12px;
                    }

                    .form-group {
                        display: flex;
                        flex-direction: column;
                        gap: 4px;
                    }

                    .form-label {
                        font-size: 12px;
                        font-weight: 500;
                        color: #374151;
                    }

                    .form-input {
                        padding: 8px 12px;
                        border: 1px solid #e5e7eb;
                        border-radius: 6px;
                        font-size: 14px;
                        color: #374151;
                        outline: none;
                        transition: border-color 0.15s ease;
                    }

                    .form-input:focus {
                        border-color: #16a34a;
                    }

                    /* Success */
                    .success-box {
                        display: flex;
                        flex-direction: column;
                        align-items: center;
                        padding: 40px 20px;
                    }

                    .success-icon {
                        width: 48px;
                        height: 48px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        background: #dcfce7;
                        color: #16a34a;
                        font-size: 24px;
                        border-radius: 50%;
                    }

                    .success-text {
                        font-size: 16px;
                        font-weight: 500;
                        color: #166534;
                        margin-top: 12px;
                    }

                    /* Error */
                    .error-box {
                        display: flex;
                        flex-direction: column;
                        align-items: center;
                        padding: 40px 20px;
                    }

                    .error-icon {
                        width: 48px;
                        height: 48px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        background: #fee2e2;
                        color: #dc2626;
                        font-size: 24px;
                        border-radius: 50%;
                    }

                    .error-text {
                        font-size: 14px;
                        color: #dc2626;
                        margin-top: 12px;
                        text-align: center;
                    }

                    .retry-btn {
                        margin-top: 16px;
                        padding: 8px 16px;
                        background: white;
                        border: 1px solid #e5e7eb;
                        border-radius: 6px;
                        font-size: 13px;
                        color: #374151;
                        cursor: pointer;
                        transition: background-color 0.15s ease;
                    }

                    .retry-btn:hover {
                        background: #f9fafb;
                    }

                    /* Footer */
                    .modal-footer {
                        display: flex;
                        justify-content: flex-end;
                        gap: 12px;
                        padding: 16px 20px;
                        border-top: 1px solid #e5e7eb;
                    }

                    .cancel-btn {
                        padding: 8px 16px;
                        background: white;
                        border: 1px solid #e5e7eb;
                        border-radius: 6px;
                        font-size: 14px;
                        color: #374151;
                        cursor: pointer;
                        transition: background-color 0.15s ease;
                    }

                    .cancel-btn:hover {
                        background: #f9fafb;
                    }

                    .save-btn {
                        padding: 8px 16px;
                        background: #16a34a;
                        border: none;
                        border-radius: 6px;
                        font-size: 14px;
                        font-weight: 500;
                        color: white;
                        cursor: pointer;
                        transition: background-color 0.15s ease;
                    }

                    .save-btn:hover:not(:disabled) {
                        background: #15803d;
                    }

                    .save-btn:disabled {
                        background: #9ca3af;
                        cursor: not-allowed;
                    }
                `}</style>
            </div>
        </div>
    );
};

export default ApplicationLogUploadModal;
